// src/pages/Inventory/StockItemsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Loader, Search, Filter, RefreshCcw, PackageOpen } from "lucide-react";
import api from "@/lib/axios"; // unified axios client (baseURL -> /solo-mdsh-api)

const API_BASE = "/inventory";

// --- tiny debounce
function useDebounced(v, ms = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return val;
}

// tolerant number + money formatting
const asMaybeNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
const money = (v) => {
  const n = asMaybeNumber(v);
  return n === null
    ? "—"
    : n.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
};

const COLS = [
  { key: "item_code", label: "Code", sortable: true },
  { key: "description", label: "Description", sortable: true },
  { key: "unit_size", label: "Unit Size", sortable: true },
  { key: "unit_price", label: "Unit Price", sortable: true, render: (r) => money(r.unit_price) },
  { key: "current_stock", label: "Stock", sortable: true, align: "right" },
  { key: "par_level", label: "Par", sortable: true, align: "right" },
  { key: "category", label: "Category", sortable: true },
  { key: "department_id", label: "Dept", sortable: true, align: "center" },
  { key: "vendor_name", label: "Vendor", sortable: false },
];

export default function StockItemsPage() {
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("vendorId") || null;

  // table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // controls
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [limit, setLimit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("item_code");
  const [dir, setDir] = useState("asc"); // asc|desc
  const [refreshing, setRefreshing] = useState(false);

  // page nav helpers
  const canPrev = offset > 0;
  const canNext = rows.length === limit; // if we got a "full" page, there may be more

  // fetch from server (search, sort, paging on backend)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const fetchList = async () => {
      let url = `${API_BASE}/stock-items`;
      const params = { sort, dir, limit, offset };
      if (vendorId) {
        url = `${API_BASE}/stock-items/by-vendor/${encodeURIComponent(vendorId)}`;
      } else if (dq.trim()) {
        params.q = dq.trim();
      }

      try {
        const { data } = await api.get(url, { params });
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data?.result)
          ? data.result
          : [];

        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) {
          // Build and display the exact URL we tried (super useful while fixing server routes)
          const tried = new URL(url, window.location.origin);
          Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") tried.searchParams.set(k, v);
          });
          const msg = e?.response?.data?.error || e?.message || "Failed to load stock items";
          setError(`${msg} — ${tried.pathname}${tried.search}`);
          console.error("Stock list fetch failed:", { tried: tried.toString(), error: e });
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchList();
    return () => {
      cancelled = true;
    };
  }, [vendorId, dq, sort, dir, limit, offset]);

  const onRefresh = async () => {
    setRefreshing(true);
    let url = `${API_BASE}/stock-items`;
    const params = { sort, dir, limit, offset };
    if (vendorId) {
      url = `${API_BASE}/stock-items/by-vendor/${encodeURIComponent(vendorId)}`;
    } else if (dq.trim()) {
      params.q = dq.trim();
    }
    try {
      const { data } = await api.get(url, { params });
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.list)
        ? data.list
        : Array.isArray(data?.result)
        ? data.result
        : [];
      setRows(list);
    } catch (e) {
      const tried = new URL(url, window.location.origin);
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") tried.searchParams.set(k, v);
      });
      console.error("Stock list refresh failed:", { tried: tried.toString(), error: e });
      toast.error(`Refresh failed — ${tried.pathname}${tried.search}`);
    } finally {
      setRefreshing(false);
    }
  };

  // header cell with sort toggling
  const header = (col) => {
    const active = sort === col.key;
    const align =
      col.align === "right"
        ? "text-right"
        : col.align === "center"
        ? "text-center"
        : "text-left";
    return (
      <th
        key={col.key}
        scope="col"
        className={`px-4 py-3 text-xs font-semibold tracking-wide cursor-pointer select-none ${align}`}
        title="Sort"
        onClick={() => {
          if (active) setDir((d) => (d === "asc" ? "desc" : "asc"));
          else {
            setSort(col.key);
            setDir("asc");
          }
          setOffset(0);
        }}
      >
        <span className="inline-flex items-center gap-1">
          {col.label}
          <span className={`text-[10px] ${active ? "text-emerald-300" : "text-white/30"}`}>
            {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
          </span>
        </span>
      </th>
    );
  };

  // total count (approx) for small hint
  const countHint = useMemo(() => {
    if (loading) return "";
    if (dq) return `${rows.length} result(s)`;
    return `${rows.length} row(s)`;
  }, [loading, dq, rows.length]);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-black via-gray-900 to-gray-950 text-white">
      {/* Title + controls */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold">📦 Stock Items</h1>
          <p className="text-gray-400 text-sm">
            {vendorId ? (
              <>Read-only view for vendor <span className="text-white font-semibold">#{vendorId}</span></>
            ) : (
              "Read-only view of all items"
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
          >
            {refreshing ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Search + page size */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            placeholder="Search by code, description, vendor, category…"
            className="w-full pl-9 pr-3 py-2 rounded bg-gray-900/60 border border-white/10"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        <select
          value={limit}
          onChange={(e) => {
            setLimit(parseInt(e.target.value, 10));
            setOffset(0);
          }}
          className="bg-gray-900/60 border border-white/10 rounded px-3 py-2"
          title="Rows per page"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}/page
            </option>
          ))}
        </select>
        <div className="text-xs text-gray-400 inline-flex items-center gap-2">
          <Filter className="w-4 h-4" />
          {countHint}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-white">
        <table className="min-w-full text-sm text-gray-900">
          <thead className="bg-gray-100">
            <tr>{COLS.map((c) => header(c))}</tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLS.length} className="px-4 py-8 text-center text-gray-600">
                  <Loader className="w-4 h-4 inline-block animate-spin mr-2" />
                  Loading…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLS.length} className="px-4 py-8 text-center text-red-600">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} className="px-4 py-10 text-center text-gray-600">
                  <div className="flex flex-col items-center gap-2">
                    <PackageOpen className="w-8 h-8" />
                    <div className="font-semibold">No items found</div>
                    <div className="text-sm">Try a different search.</div>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.item_id} className="odd:bg-white even:bg-gray-50">
                  {COLS.map((c) => {
                    const align =
                      c.align === "right"
                        ? "text-right"
                        : c.align === "center"
                        ? "text-center"
                        : "text-left";
                    const content = c.render ? c.render(r) : r[c.key] ?? "—";
                    return (
                      <td key={c.key} className={`px-4 py-2 ${align}`}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pager */}
      <div className="flex items-center justify-between p-4 border border-t-0 border-white/10 rounded-b-xl bg-[#0f1833]/40 mt-[-1px]">
        <div className="text-xs text-white/60">
          Offset <strong>{offset}</strong> • Page size <strong>{limit}</strong>
        </div>
        <div className="flex gap-2">
          <button
            className={`px-3 py-1 rounded-lg border border-white/10 ${canPrev ? "hover:bg-white/10" : "opacity-50 cursor-not-allowed"}`}
            onClick={() => canPrev && setOffset(Math.max(0, offset - limit))}
            disabled={!canPrev}
          >
            ← Prev
          </button>
          <button
            className={`px-3 py-1 rounded-lg border border-white/10 ${canNext ? "hover:bg-white/10" : "opacity-50 cursor-not-allowed"}`}
            onClick={() => canNext && setOffset(offset + limit)}
            disabled={!canNext}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
